import { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useQuery } from 'react-query'

import { useFeatureFlag, FeatureFlagKeys } from '_hooks/useFeatureFlag'
import { usePermissions } from '_stores/permissions.store'
import { fetchSiteGatewayForCapital, createCapitalSessionSecret } from '_services/capital.service'
import { CAPITAL_SITE_PAYMENT_GATEWAY_QUERY_KEY } from '_services/graphql/capital.queries'
import { loadStripeConnectSdk, type StripeConnectInstance } from '_services/stripe-connect.service'

import { isCapitalAvailable } from './capital.utils'
import { buildCapitalAppearance, CAPITAL_FONT_CSS_SRC } from './capital.appearance'

/**
 * Whether this location may see Capital at all — drives both the left-nav item and the page.
 *
 * The gateway lookup only runs once the flag and the permission already pass, so a location with
 * Capital switched off never pays for the extra query.
 */
export const useCapitalAccess = (siteId?: string | null) => {
  const { value: isFlagEnabled, isLoading: isFlagLoading } = useFeatureFlag(
    FeatureFlagKeys.CapitalEnabled,
    { defaultValue: false }
  )
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()

  const isFlagOn = Boolean(isFlagEnabled)
  const hasCapitalPermission = hasPermission('access__capital')
  // Both the flag and the permissions arrive asynchronously and read as "denied" until they do.
  // Without this, the page would render "Capital is not available" on every load before flipping
  // to the real answer. Keyed on the loading flags, not on the parsed value — a user who
  // genuinely holds no permissions must resolve to "denied", not spin forever.
  const isGateResolving = isFlagLoading || isPermissionsLoading
  const isEnabled = !isGateResolving && isFlagOn && hasCapitalPermission && Boolean(siteId)

  // `fetchSiteGatewayForCapital` resolves to nulls rather than throwing for a site with no active
  // gateway, so an un-onboarded location simply reads as ineligible. No retry: the underlying
  // query is an alertable 500 by design on that path, and retrying would multiply the alert.
  const { data, isLoading: isGatewayLoading } = useQuery(
    [CAPITAL_SITE_PAYMENT_GATEWAY_QUERY_KEY, { siteId }],
    () => fetchSiteGatewayForCapital(siteId as string),
    { enabled: isEnabled, retry: false }
  )

  return {
    isAvailable: isCapitalAvailable({
      isFlagEnabled: isFlagOn,
      hasCapitalPermission,
      siteId,
      gateway: data,
    }),
    isLoading: isGateResolving || (isEnabled && isGatewayLoading),
    publishableKey: data?.paymentGatewayPublishableKey ?? null,
  }
}

/**
 * A Connect instance for this site's connected account, ready to `create()` embedded components.
 *
 * The account session is handed over as `fetchClientSecret` rather than a resolved secret so
 * Connect can mint a fresh one itself when the current session expires — a financing application
 * left open while someone steps away must not dead-end partway through.
 */
export const useStripeConnectInstance = (siteId: string, publishableKey: string) => {
  const [connectInstance, setConnectInstance] = useState<StripeConnectInstance | null>(null)
  const [hasError, setHasError] = useState(false)
  const { palette } = useTheme()
  const appearance = useMemo(() => buildCapitalAppearance(palette), [palette])
  // Read through a ref so a colour mode switch restyles the live instance below instead of
  // re-initialising it, which would remount Capital and lose an application in progress.
  const appearanceRef = useRef(appearance)
  appearanceRef.current = appearance

  useEffect(() => {
    let isCancelled = false
    setHasError(false)

    // `init()` resolves without contacting Stripe — the account session is only minted later,
    // when a component first mounts and calls fetchClientSecret. A rejection there happens
    // inside Connect and never reaches React on its own, which renders an empty panel instead
    // of an error. So the fetch is wrapped: surface the failure here, then rethrow so Connect
    // still sees the rejection and stops trying to mount.
    const fetchClientSecret = async (): Promise<string> => {
      try {
        return await createCapitalSessionSecret(siteId)
      } catch (error) {
        console.error('[Capital] Failed to create the account session:', error)
        if (!isCancelled) {
          setHasError(true)
        }
        throw error
      }
    }

    void loadStripeConnectSdk()
      .then((stripeConnect) => {
        if (isCancelled) {
          return
        }
        setConnectInstance(
          stripeConnect.init({
            publishableKey,
            fetchClientSecret,
            appearance: appearanceRef.current,
            fonts: [{ cssSrc: CAPITAL_FONT_CSS_SRC }],
          })
        )
      })
      .catch((error: unknown) => {
        console.error('[Capital] Failed to initialize Stripe Connect:', error)
        if (!isCancelled) {
          setHasError(true)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [siteId, publishableKey])

  useEffect(() => {
    connectInstance?.update({ appearance })
  }, [connectInstance, appearance])

  return { connectInstance, hasError, setHasError }
}
